import React, { useState } from "react";
import { useDispatch } from "react-redux";
import Input from "../../../components/common/Input";
import Select from "../../../components/common/Select";
import Button from "../../../components/common/Button";
import { createAdminThunk } from "../../../store/thunks/adminThunks";
import { UserAddIcon, ShieldCheckIcon } from "@heroicons/react/solid";

const CreateAdminStep = ({ onComplete }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
    role: "manager",
    rolePreset: "branch_manager",
    branchAccess: {
      primaryBranch: "",
      accessLevel: "FULL",
    },
    accessControl: {
      rates: { enabled: false },
      products: { enabled: true },
      customers: { enabled: true },
      reports: { enabled: false },
      branches: { enabled: false },
      admins: { enabled: false },
    },
  });

  const roleOptions = [
    { value: "manager", label: "Manager" },
    { value: "staff", label: "Staff" },
    { value: "viewer", label: "Viewer" },
  ];

  const rolePresetOptions = [
    { value: "branch_manager", label: "Branch Manager - Full branch access" },
    { value: "sales_staff", label: "Sales Staff - Sales operations only" },
    { value: "accountant", label: "Accountant - Financial reports access" },
    { value: "viewer", label: "Viewer - Read-only access" },
    { value: "custom", label: "Custom - Configure manually" },
  ];

  const accessLevelOptions = [
    { value: "FULL", label: "Full Access" },
    { value: "EDIT", label: "Edit Access" },
    { value: "VIEW", label: "View Only" },
  ];

  const handleChange = (field, value) => {
    if (field.includes(".")) {
      const parts = field.split(".");
      if (parts.length === 2) {
        const [parent, child] = parts;
        setFormData({
          ...formData,
          [parent]: {
            ...formData[parent],
            [child]: value,
          },
        });
      }
    } else {
      setFormData({
        ...formData,
        [field]: value,
      });
    }

    // Clear error for this field
    if (errors[field]) {
      setErrors({ ...errors, [field]: "" });
    }
  };

  const handlePresetChange = (preset) => {
    setFormData({ ...formData, rolePreset: preset });

    // Apply preset permissions
    switch (preset) {
      case "branch_manager":
        setFormData({
          ...formData,
          rolePreset: preset,
          role: "manager",
          accessControl: {
            rates: { enabled: true },
            products: { enabled: true },
            customers: { enabled: true },
            reports: { enabled: true },
            branches: { enabled: false },
            admins: { enabled: false },
          },
        });
        break;

      case "sales_staff":
        setFormData({
          ...formData,
          rolePreset: preset,
          role: "staff",
          accessControl: {
            rates: { enabled: false },
            products: { enabled: true },
            customers: { enabled: true },
            reports: { enabled: false },
            branches: { enabled: false },
            admins: { enabled: false },
          },
        });
        break;

      case "accountant":
        setFormData({
          ...formData,
          rolePreset: preset,
          role: "staff",
          accessControl: {
            rates: { enabled: false },
            products: { enabled: false },
            customers: { enabled: false },
            reports: { enabled: true },
            branches: { enabled: false },
            admins: { enabled: false },
          },
        });
        break;

      case "viewer":
        setFormData({
          ...formData,
          rolePreset: preset,
          role: "viewer",
          branchAccess: {
            ...formData.branchAccess,
            accessLevel: "VIEW",
          },
          accessControl: {
            rates: { enabled: false },
            products: { enabled: false },
            customers: { enabled: false },
            reports: { enabled: false },
            branches: { enabled: false },
            admins: { enabled: false },
          },
        });
        break;

      default:
        break;
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name) newErrors.name = "Name is required";
    if (!formData.email) newErrors.email = "Email is required";
    if (!formData.password) newErrors.password = "Password is required";
    if (!formData.confirmPassword)
      newErrors.confirmPassword = "Please confirm password";

    // Email validation
    if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    // Phone validation if provided
    if (formData.phoneNumber && !/^[6-9]\d{9}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = "Invalid phone number format";
    }

    // Password validation
    if (formData.password && formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      const adminData = {
        ...formData,
        confirmPassword: undefined, // Remove confirmPassword before sending
      };

      const result = await dispatch(createAdminThunk(adminData)).unwrap();

      onComplete({
        adminCreated: true,
        adminId: result._id,
        adminEmail: result.email,
      });
    } catch (error) {
      setErrors({
        general: error || "Failed to create admin",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    onComplete({ skipped: true });
  };

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-2">Create Additional Admin</h3>
        <p className="text-gray-600">
          Add another admin user to help manage your system. You can add more
          admins later.
        </p>
      </div>

      {errors.general && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {errors.general}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Personal Information */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium mb-4 flex items-center">
            <UserAddIcon className="w-5 h-5 mr-2" />
            Personal Information
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Full Name"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              error={errors.name}
              placeholder="Admin name"
              required
            />
            <Input
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              error={errors.email}
              placeholder="admin@example.com"
              required
            />
            <Input
              label="Phone Number (Optional)"
              value={formData.phoneNumber}
              onChange={(e) => handleChange("phoneNumber", e.target.value)}
              error={errors.phoneNumber}
              placeholder="10-digit mobile number"
              maxLength="10"
            />
          </div>
        </div>

        {/* Password */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium mb-4">Set Password</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Password"
              type="password"
              value={formData.password}
              onChange={(e) => handleChange("password", e.target.value)}
              error={errors.password}
              placeholder="Minimum 8 characters"
              required
            />
            <Input
              label="Confirm Password"
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => handleChange("confirmPassword", e.target.value)}
              error={errors.confirmPassword}
              placeholder="Re-enter password"
              required
            />
          </div>
        </div>

        {/* Role & Permissions */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium mb-4 flex items-center">
            <ShieldCheckIcon className="w-5 h-5 mr-2" />
            Role & Permissions
          </h4>

          <div className="space-y-4">
            <Select
              label="Role Preset"
              value={formData.rolePreset}
              onChange={(e) => handlePresetChange(e.target.value)}
              options={rolePresetOptions}
            />

            {formData.rolePreset === "custom" && (
              <>
                <Select
                  label="Admin Role"
                  value={formData.role}
                  onChange={(e) => handleChange("role", e.target.value)}
                  options={roleOptions}
                />

                <Select
                  label="Access Level"
                  value={formData.branchAccess.accessLevel}
                  onChange={(e) =>
                    handleChange("branchAccess.accessLevel", e.target.value)
                  }
                  options={accessLevelOptions}
                />

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Module Permissions
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.entries(formData.accessControl).map(
                      ([module, config]) => (
                        <label
                          key={module}
                          className="flex items-center space-x-2"
                        >
                          <input
                            type="checkbox"
                            checked={config.enabled}
                            onChange={(e) =>
                              handleChange(`accessControl.${module}`, {
                                ...config,
                                enabled: e.target.checked,
                              })
                            }
                            className="rounded border-gray-300"
                          />
                          <span className="text-sm capitalize">{module}</span>
                        </label>
                      )
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Preview of Selected Role */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-2">Permission Summary</h4>
          <div className="text-sm text-blue-800">
            <p className="mb-1">
              <strong>Role:</strong>{" "}
              {formData.role.charAt(0).toUpperCase() + formData.role.slice(1)}
            </p>
            <p className="mb-1">
              <strong>Access Level:</strong> {formData.branchAccess.accessLevel}
            </p>
            <p>
              <strong>Enabled Modules:</strong>{" "}
              {Object.entries(formData.accessControl)
                .filter(([_, config]) => config.enabled)
                .map(
                  ([module]) => module.charAt(0).toUpperCase() + module.slice(1)
                )
                .join(", ") || "None"}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between">
          <Button
            type="button"
            variant="secondary-outline"
            onClick={handleSkip}
          >
            Skip for Now
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={loading}
            disabled={loading}
          >
            Create Admin & Continue
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateAdminStep;
